/**
 * The replayer.
 *
 * One state machine serves both ProTracker and FastTracker II. Where the two
 * genuinely disagree — portamento step size, vibrato depth scaling, what an
 * out-of-range sample offset does, whether commands remember their parameters —
 * the difference is switched on `song.mode` and commented at the point of use,
 * because those divergences are exactly what "compatible" means here.
 */
import {
  CELL_EFFECT,
  CELL_INSTRUMENT,
  CELL_NOTE,
  CELL_PARAM,
  CELL_STRIDE,
  CELL_VOLUME,
  LOOP_NONE,
  NOTE_KEYOFF,
  type Envelope,
  type Instrument,
  type Sample,
  type Song,
} from '../types';
import {
  FT2_AMIGA_CLOCK,
  PAL_CLOCK,
  PT_MAX_PERIOD,
  PT_MIN_PERIOD,
  PT_SINE_TABLE,
  ft2AmigaPeriod,
  ft2LinearFrequency,
  ft2LinearPeriod,
  ptPeriod,
} from '../constants';
import { Channel } from './channel';

export interface PlayerOptions {
  /** Treat MOD effect 8xx as "set panning". Every modern player does; PT ignored it. */
  modPanningCommand: boolean;
  /** Loop back to the restart position instead of stopping at the end. */
  loopSong: boolean;
}

export const DEFAULT_PLAYER_OPTIONS: PlayerOptions = {
  modPanningCommand: true,
  loopSong: true,
};

/** Volume-change table for the XM multi-retrigger command (Rxy). */
const RETRIG_VOLUME: readonly ((v: number) => number)[] = [
  (v) => v,
  (v) => v - 1,
  (v) => v - 2,
  (v) => v - 4,
  (v) => v - 8,
  (v) => v - 16,
  (v) => (v * 2) / 3,
  (v) => v / 2,
  (v) => v,
  (v) => v + 1,
  (v) => v + 2,
  (v) => v + 4,
  (v) => v + 8,
  (v) => v + 16,
  (v) => (v * 3) / 2,
  (v) => v * 2,
];

/** ProTracker's invert-loop speed table, indexed by the EFx parameter. */
const FUNK_TABLE = [0, 5, 6, 7, 8, 10, 11, 13, 16, 19, 22, 26, 32, 43, 64, 128];

/** Voices reserved for previewing notes outside the song's own channels. */
export const PREVIEW_VOICES = 8;

export class Player {
  song: Song;
  sampleRate: number;
  options: PlayerOptions;

  channels: Channel[] = [];

  speed = 6;
  tempo = 125;
  globalVolume = 64;

  /** Position in the order table. */
  order = 0;
  row = 0;
  tick = 0;
  pattern = 0;

  /** True once the song has run past its end (or hit F00). */
  songEnded = false;
  /** Increments each time playback wraps — lets the UI show a loop count. */
  loopCount = 0;
  /** Set by E0x; the mixer reads it to switch the Amiga's LED filter. */
  ledFilter = false;

  /**
   * Extra voices that do not belong to the song, used for previewing notes from
   * the keyboard and the instrument list. They run through the same update path
   * as song channels so a previewed note gets identical envelopes and filtering.
   */
  previewChannels: Channel[] = [];
  /** `channels` followed by `previewChannels`; what the mixer renders. */
  renderChannels: Channel[] = [];

  private patternDelayRows = 0;
  /**
   * True while a row is repeating under EEx.
   *
   * Separate from the counter because the *last* repeat runs with the counter
   * already at zero — without this flag that pass looks identical to a fresh
   * row, so the EEx on it re-arms the delay and the pattern never advances.
   */
  private patternDelayActive = false;
  private jumpOrder = -1;
  private jumpRow = -1;
  private jumpPending = false;

  constructor(song: Song, sampleRate: number, options: Partial<PlayerOptions> = {}) {
    this.song = song;
    this.sampleRate = sampleRate;
    this.options = { ...DEFAULT_PLAYER_OPTIONS, ...options };
    this.reset();
  }

  setSong(song: Song): void {
    this.song = song;
    this.reset();
  }

  reset(): void {
    const n = this.song.channels;
    this.channels = [];
    for (let i = 0; i < n; i++) {
      const ch = new Channel();
      ch.reset(i, this.song.defaultPanning[i] ?? 128);
      this.channels.push(ch);
    }
    if (this.previewChannels.length === 0) this.initPreviewVoices(PREVIEW_VOICES);
    this.rebuildRenderChannels();
    this.speed = this.song.initialSpeed;
    this.tempo = this.song.initialTempo;
    this.globalVolume = 64;
    this.order = 0;
    this.row = 0;
    this.tick = 0;
    this.pattern = this.song.order[0] ?? 0;
    this.songEnded = false;
    this.loopCount = 0;
    this.ledFilter = false;
    this.patternDelayRows = 0;
    this.patternDelayActive = false;
    this.jumpOrder = -1;
    this.jumpRow = -1;
    this.jumpPending = false;
  }

  /** Jump the transport to an order position, as the UI's order list does. */
  seek(order: number, row = 0): void {
    this.order = Math.max(0, Math.min(Math.max(0, this.song.songLength - 1), order));
    this.pattern = this.song.order[this.order] ?? 0;
    const pattern = this.currentPattern();
    this.row = Math.max(0, Math.min(pattern.rows - 1, row));
    this.tick = 0;
    this.patternDelayRows = 0;
    this.patternDelayActive = false;
    this.jumpPending = false;
    this.jumpOrder = -1;
    this.jumpRow = -1;
    this.songEnded = false;
    for (const ch of this.channels) {
      ch.stop();
      ch.patternLoopCount = 0;
      ch.patternLoopRow = 0;
    }
  }

  /** Silence every voice without moving the transport. */
  allNotesOff(): void {
    for (const ch of this.renderChannels) ch.stop();
  }

  private initPreviewVoices(count: number): void {
    this.previewChannels = [];
    for (let i = 0; i < count; i++) {
      const ch = new Channel();
      ch.reset(this.song.channels + i, 128);
      this.previewChannels.push(ch);
    }
  }

  private rebuildRenderChannels(): void {
    this.renderChannels = this.channels.concat(this.previewChannels);
  }

  /**
   * Start a note on a preview voice — the tracker keyboard and the "click an
   * instrument to hear it" behaviour both land here.
   */
  previewNoteOn(voice: number, note: number, instrumentIndex: number, volume = 64): void {
    const ch = this.previewChannels[voice % this.previewChannels.length];
    if (!ch) return;
    ch.instrument = null;
    ch.sample = null;
    this.setInstrument(ch, instrumentIndex);
    if (!ch.instrument) return;
    this.startNote(ch, note - 1, true);
    ch.volume = Math.min(64, volume);
    ch.panning = 128;
  }

  /** Release a preview voice; the instrument's fadeout takes it from here. */
  previewNoteOff(voice: number): void {
    const ch = this.previewChannels[voice % this.previewChannels.length];
    if (!ch) return;
    if (this.song.mode === 'ft2') this.noteOff(ch);
    else ch.stop();
  }

  previewAllOff(): void {
    for (const ch of this.previewChannels) ch.stop();
  }

  /** How many output frames one tick lasts at the current tempo. */
  get samplesPerTick(): number {
    return (this.sampleRate * 2.5) / this.tempo;
  }

  currentPattern() {
    const idx = this.song.order[this.order] ?? 0;
    return this.song.patterns[idx] ?? this.song.patterns[0];
  }

  // =======================================================================
  // Tick driver
  // =======================================================================

  /** Advance the replayer by exactly one tick. */
  doTick(): void {
    if (this.tick === 0) {
      // A delayed row re-runs its effects but must not retrigger notes — FT2
      // behaviour, and what modules using EEx alongside slides expect.
      this.processRow(this.patternDelayActive);
    } else {
      this.processTick();
    }

    this.updateChannels();

    this.tick++;
    if (this.tick >= this.speed) {
      this.tick = 0;
      if (this.patternDelayRows > 0) {
        this.patternDelayRows--;
        this.patternDelayActive = true;
      } else {
        this.patternDelayActive = false;
        this.advanceRow();
      }
    }
  }

  private advanceRow(): void {
    if (this.jumpPending) {
      this.jumpPending = false;
      const targetOrder = this.jumpOrder >= 0 ? this.jumpOrder : this.order + 1;
      const targetRow = this.jumpRow >= 0 ? this.jumpRow : 0;
      this.jumpOrder = -1;
      this.jumpRow = -1;
      this.setOrder(targetOrder, targetRow);
      return;
    }

    const pattern = this.currentPattern();
    this.row++;
    if (this.row >= pattern.rows) {
      this.setOrder(this.order + 1, 0);
    }
  }

  private setOrder(order: number, row: number): void {
    let next = order;
    if (next >= this.song.songLength) {
      this.songEnded = true;
      this.loopCount++;
      if (!this.options.loopSong) return;
      next = this.song.restartPosition < this.song.songLength ? this.song.restartPosition : 0;
    }

    const changedPattern = next !== this.order;
    this.order = next;
    this.pattern = this.song.order[next] ?? 0;
    const pattern = this.currentPattern();
    this.row = Math.max(0, Math.min(row, pattern.rows - 1));

    // Pattern-loop counters are scoped to a pattern. Resetting them on an E6x
    // jump back into the *same* pattern would restart the loop forever, so this
    // only fires when the order position actually moved.
    if (changedPattern) {
      for (const ch of this.channels) {
        ch.patternLoopCount = 0;
        ch.patternLoopRow = 0;
      }
    }
  }

  // =======================================================================
  // Row / tick processing
  // =======================================================================

  private processRow(isDelayRepeat: boolean): void {
    const pattern = this.currentPattern();
    const nch = this.song.channels;

    for (let c = 0; c < nch; c++) {
      const ch = this.channels[c];
      const off = (this.row * nch + c) * CELL_STRIDE;
      const note = pattern.data[off + CELL_NOTE];
      const instrument = pattern.data[off + CELL_INSTRUMENT];
      const volumeColumn = pattern.data[off + CELL_VOLUME];
      const effect = pattern.data[off + CELL_EFFECT];
      const param = pattern.data[off + CELL_PARAM];

      // Fresh row: clear the modulation that only lasts a row.
      ch.tremoloVolume = 0;
      ch.pitchModulated = false;
      ch.noteDelayed = false;
      if (effect !== 0x1d) ch.tremorMute = false;

      // EDx defers the note (but not the effect) to a later tick in this row.
      const isNoteDelay = effect === 0x0e && (param >> 4) === 0x0d && (param & 0x0f) !== 0;
      if (isNoteDelay && !isDelayRepeat) {
        ch.noteDelayed = true;
        ch.delayedNote = note;
        ch.delayedInstrument = instrument;
        ch.delayedVolumeColumn = volumeColumn;
        this.effectTick0(ch, effect, param);
        continue;
      }

      if (!isDelayRepeat) {
        this.triggerCell(ch, note, instrument, volumeColumn, effect);
      }
      this.effectTick0(ch, effect, param);
    }
  }

  private processTick(): void {
    const pattern = this.currentPattern();
    const nch = this.song.channels;

    for (let c = 0; c < nch; c++) {
      const ch = this.channels[c];
      const off = (this.row * nch + c) * CELL_STRIDE;
      const effect = pattern.data[off + CELL_EFFECT];
      const param = pattern.data[off + CELL_PARAM];
      const volumeColumn = pattern.data[off + CELL_VOLUME];

      ch.pitchModulated = false;

      // A pending EDx fires here.
      if (ch.noteDelayed) {
        const delay = effect === 0x0e ? param & 0x0f : 0;
        if (this.tick >= delay) {
          ch.noteDelayed = false;
          this.triggerCell(ch, ch.delayedNote, ch.delayedInstrument, ch.delayedVolumeColumn, 0);
        }
      }

      if (this.song.mode === 'ft2' && volumeColumn > 0) {
        this.volumeColumnTick(ch, volumeColumn);
      }

      this.effectTick(ch, effect, param);
      this.funkRepeat(ch);
    }
  }

  /**
   * Apply a pattern cell's note/instrument/volume fields.
   *
   * Ordering here is FT2's and it matters: the instrument is latched first, so a
   * bare instrument column resets volume without restarting the sample, and the
   * note then either retriggers the voice or — under tone portamento — only
   * moves the slide target.
   */
  private triggerCell(
    ch: Channel,
    note: number,
    instrument: number,
    volumeColumn: number,
    effect: number,
  ): void {
    const isTonePorta =
      effect === 0x03 ||
      effect === 0x05 ||
      (this.song.mode === 'ft2' && volumeColumn >= 0xf0);

    if (instrument > 0) {
      this.setInstrument(ch, instrument);
    }

    if (note === NOTE_KEYOFF) {
      this.noteOff(ch);
    } else if (note > 0) {
      if (isTonePorta && ch.sample) {
        this.setPortaTarget(ch, note - 1);
      } else {
        this.startNote(ch, note - 1, instrument > 0);
      }
    } else if (instrument > 0) {
      // Instrument column alone: FT2 resets volume/pan from the sample and
      // restarts the envelopes, but leaves the sample position untouched.
      this.resetVolumeFromSample(ch);
      if (this.song.mode === 'ft2') this.resetEnvelopes(ch);
    }

    if (this.song.mode === 'ft2' && volumeColumn > 0) {
      this.volumeColumnTick0(ch, volumeColumn);
    }
  }

  private setInstrument(ch: Channel, index: number): void {
    ch.instrumentIndex = index;
    const inst = this.song.instruments[index - 1];
    // A reference to a missing instrument leaves the channel exactly as it was.
    if (!inst) return;
    ch.instrument = inst;
    const sample = this.resolveSample(inst, ch.triggerNote);
    if (sample) {
      ch.sample = sample;
      ch.finetune = sample.finetune;
      ch.relativeNote = sample.relativeNote;
    }
  }

  private resolveSample(inst: Instrument, note: number): Sample | null {
    if (!inst.samples.length) return null;
    const mapped = note >= 0 && note < 96 ? inst.sampleMap[note] : 0;
    return inst.samples[mapped] ?? inst.samples[0] ?? null;
  }

  private resetVolumeFromSample(ch: Channel): void {
    if (!ch.sample) return;
    ch.volume = ch.sample.volume;
    if (this.song.mode === 'ft2') ch.panning = ch.sample.panning;
  }

  private resetEnvelopes(ch: Channel): void {
    ch.volumeEnvelopeTick = 0;
    ch.panningEnvelopeTick = 0;
    ch.volumeEnvelopeDone = false;
    ch.panningEnvelopeDone = false;
    ch.fadeoutVolume = 65536;
    ch.keyOff = false;
    ch.autoVibratoPos = 0;
    ch.autoVibratoSweep = 0;
  }

  private startNote(ch: Channel, note: number, hadInstrument: boolean): void {
    ch.triggerNote = note;

    if (ch.instrument) {
      const sample = this.resolveSample(ch.instrument, note);
      if (sample) {
        ch.sample = sample;
        ch.finetune = sample.finetune;
        ch.relativeNote = sample.relativeNote;
      }
    }
    if (!ch.sample) return;

    ch.note = note;
    ch.period = this.noteToPeriod(note + ch.relativeNote, ch.finetune);
    ch.portaTarget = ch.period;
    ch.pitchModulated = false;

    ch.position = ch.funkOffset || 0;
    ch.funkOffset = 0;
    ch.direction = 1;
    ch.playing = true;

    // A note with no instrument column keeps the volume the channel already had
    // — true of both ProTracker and FastTracker II.
    if (hadInstrument) this.resetVolumeFromSample(ch);
    this.resetEnvelopes(ch);

    // Oscillator positions reset unless the E4x/E7x "continue" bit is set.
    if (!ch.vibratoNoRetrig) ch.vibratoPos = 0;
    if (!ch.tremoloNoRetrig) ch.tremoloPos = 0;
    ch.tremorCount = 0;
    ch.tremorOn = false;
    ch.tremorMute = false;
  }

  private noteOff(ch: Channel): void {
    ch.keyOff = true;
    if (this.song.mode === 'pt') {
      // MOD has no key-off; if the value shows up anyway, silence is the safe read.
      ch.volume = 0;
      return;
    }
    // FT2: with no volume envelope there is nothing to fade, so the note dies.
    if (!ch.instrument || !ch.instrument.volumeEnvelope.enabled) {
      ch.volume = 0;
    }
  }

  private setPortaTarget(ch: Channel, note: number): void {
    ch.triggerNote = note;
    if (!ch.sample) return;
    ch.note = note;
    ch.portaTarget = this.noteToPeriod(note + ch.relativeNote, ch.finetune);
  }

  // =======================================================================
  // Pitch helpers
  // =======================================================================

  noteToPeriod(note: number, finetune: number): number {
    if (this.song.mode === 'pt') return ptPeriod(note, finetune);
    if (this.song.linearFrequency) return ft2LinearPeriod(note, finetune);
    return ft2AmigaPeriod(note, finetune);
  }

  periodToFrequency(period: number): number {
    if (period <= 0) return 0;
    if (this.song.mode === 'pt') return PAL_CLOCK / period;
    if (this.song.linearFrequency) return ft2LinearFrequency(period);
    return FT2_AMIGA_CLOCK / period;
  }

  /** Clamp a base period to what the target hardware could actually produce. */
  private clampPeriod(period: number): number {
    if (this.song.mode === 'pt' && this.song.amigaLimits) {
      return Math.max(PT_MIN_PERIOD, Math.min(PT_MAX_PERIOD, period));
    }
    if (this.song.linearFrequency) return Math.max(0, Math.min(7743, period));
    return Math.max(1, Math.min(32767, period));
  }

  /** Snap a period to the nearest semitone, for glissando (E3x). */
  private snapToSemitone(ch: Channel, period: number): number {
    let best = period;
    let bestDist = Infinity;
    for (let n = 0; n < 120; n++) {
      const p = this.noteToPeriod(n + ch.relativeNote, ch.finetune);
      const d = Math.abs(p - period);
      if (d < bestDist) {
        bestDist = d;
        best = p;
      }
    }
    return best;
  }

  /**
   * ProTracker periods are a quarter the size of FastTracker's, so every
   * portamento step is scaled to match or slides move four times too far.
   */
  private portaScale(): number {
    return this.song.mode === 'pt' ? 1 : 4;
  }

  // =======================================================================
  // Effects — tick 0
  // =======================================================================

  private effectTick0(ch: Channel, effect: number, param: number): void {
    const x = param >> 4;
    const y = param & 0x0f;

    switch (effect) {
      case 0x00: // Arpeggio
        if (param !== 0) ch.memArpeggio = param;
        break;

      case 0x01: // Portamento up — ProTracker has no parameter memory here
        if (param !== 0) ch.memPortaUp = param;
        else if (this.song.mode === 'pt') ch.memPortaUp = 0;
        break;

      case 0x02: // Portamento down
        if (param !== 0) ch.memPortaDown = param;
        else if (this.song.mode === 'pt') ch.memPortaDown = 0;
        break;

      case 0x03: // Tone portamento
        if (param !== 0) ch.memTonePorta = param;
        break;

      case 0x04: // Vibrato
        if (x !== 0) ch.memVibratoSpeed = x;
        if (y !== 0) ch.memVibratoDepth = y;
        break;

      case 0x05: // Tone portamento + volume slide
      case 0x06: // Vibrato + volume slide
        if (param !== 0) ch.memVolumeSlide = param;
        break;

      case 0x07: // Tremolo
        if (x !== 0) ch.memTremoloSpeed = x;
        if (y !== 0) ch.memTremoloDepth = y;
        break;

      case 0x08: // Set panning
        if (this.song.mode === 'ft2' || this.options.modPanningCommand) ch.panning = param;
        break;

      case 0x09: // Sample offset
        this.sampleOffset(ch, param);
        break;

      case 0x0a: // Volume slide
        if (param !== 0) ch.memVolumeSlide = param;
        break;

      case 0x0b: // Position jump
        this.jumpOrder = param;
        if (this.jumpRow < 0) this.jumpRow = 0;
        this.jumpPending = true;
        break;

      case 0x0c: // Set volume
        ch.volume = Math.max(0, Math.min(64, param));
        break;

      case 0x0d: // Pattern break — the parameter is decimal-coded, not hex
        this.jumpRow = x * 10 + y;
        this.jumpPending = true;
        break;

      case 0x0e:
        this.extendedTick0(ch, x, y);
        break;

      case 0x0f: // Set speed / tempo
        if (param === 0) {
          this.songEnded = true; // F00 halts ProTracker
        } else if (param < 32) {
          this.speed = param;
        } else {
          this.tempo = param;
        }
        break;

      // ---- XM-only commands ----------------------------------------------
      case 0x10: // Gxx — set global volume
        if (this.song.mode === 'ft2') this.globalVolume = Math.min(64, param);
        break;

      case 0x11: // Hxy — global volume slide
        if (this.song.mode === 'ft2' && param !== 0) ch.memGlobalVolumeSlide = param;
        break;

      case 0x14: // Kxx — key off at tick xx
        if (this.song.mode === 'ft2' && param === 0) this.noteOff(ch);
        break;

      case 0x15: // Lxx — set envelope position
        if (this.song.mode === 'ft2') {
          ch.volumeEnvelopeTick = param;
          ch.panningEnvelopeTick = param;
        }
        break;

      case 0x19: // Pxy — panning slide
        if (this.song.mode === 'ft2' && param !== 0) ch.memPanningSlide = param;
        break;

      case 0x1b: // Rxy — multi retrigger
        if (this.song.mode === 'ft2') {
          if (y !== 0) ch.memRetrig = (ch.memRetrig & 0xf0) | y;
          if (x !== 0) ch.memRetrig = (ch.memRetrig & 0x0f) | (x << 4);
        }
        break;

      case 0x1d: // Txy — tremor
        if (this.song.mode === 'ft2' && param !== 0) ch.memTremor = param;
        break;

      case 0x21: // Xxy — extra fine portamento (single period units)
        if (this.song.mode === 'ft2') {
          if (x === 1) {
            if (y !== 0) ch.memExtraFinePortaUp = y;
            ch.period = this.clampPeriod(ch.period - ch.memExtraFinePortaUp);
          } else if (x === 2) {
            if (y !== 0) ch.memExtraFinePortaDown = y;
            ch.period = this.clampPeriod(ch.period + ch.memExtraFinePortaDown);
          }
        }
        break;

      default:
        break;
    }
  }

  private extendedTick0(ch: Channel, x: number, y: number): void {
    switch (x) {
      case 0x0: // E0x — Amiga LED filter (0 = on, 1 = off)
        this.ledFilter = y === 0;
        break;

      case 0x1: // E1x — fine portamento up
        if (y !== 0) ch.memFinePortaUp = y;
        ch.period = this.clampPeriod(ch.period - this.portaScale() * ch.memFinePortaUp);
        break;

      case 0x2: // E2x — fine portamento down
        if (y !== 0) ch.memFinePortaDown = y;
        ch.period = this.clampPeriod(ch.period + this.portaScale() * ch.memFinePortaDown);
        break;

      case 0x3: // E3x — glissando control
        ch.glissando = y !== 0;
        break;

      case 0x4: // E4x — vibrato waveform
        ch.vibratoWaveform = y & 0x03;
        ch.vibratoNoRetrig = (y & 0x04) !== 0;
        break;

      case 0x5: // E5x — set finetune (signed nibble in MOD units)
        ch.finetune = (y > 7 ? y - 16 : y) * 16;
        if (ch.playing) ch.period = this.noteToPeriod(ch.note + ch.relativeNote, ch.finetune);
        break;

      case 0x6: // E6x — pattern loop
        if (y === 0) {
          ch.patternLoopRow = this.row;
        } else {
          if (ch.patternLoopCount === 0) ch.patternLoopCount = y;
          else ch.patternLoopCount--;
          if (ch.patternLoopCount > 0) {
            this.jumpRow = ch.patternLoopRow;
            this.jumpOrder = this.order;
            this.jumpPending = true;
          }
        }
        break;

      case 0x7: // E7x — tremolo waveform
        ch.tremoloWaveform = y & 0x03;
        ch.tremoloNoRetrig = (y & 0x04) !== 0;
        break;

      case 0x8: // E8x — coarse panning in FT2; ProTracker ignored it
        if (this.song.mode === 'ft2') ch.panning = y * 17;
        break;

      case 0x9: // E9x — retrigger, handled per tick
        break;

      case 0xa: // EAx — fine volume slide up
        if (y !== 0) ch.memFineVolumeUp = y;
        ch.volume = Math.min(64, ch.volume + ch.memFineVolumeUp);
        break;

      case 0xb: // EBx — fine volume slide down
        if (y !== 0) ch.memFineVolumeDown = y;
        ch.volume = Math.max(0, ch.volume - ch.memFineVolumeDown);
        break;

      case 0xc: // ECx — note cut (x = 0 cuts immediately)
        if (y === 0) ch.volume = 0;
        break;

      case 0xd: // EDx — note delay, resolved in processRow/processTick
        break;

      case 0xe: // EEx — pattern delay (ignored on the repeats it causes)
        if (!this.patternDelayActive) this.patternDelayRows = y;
        break;

      case 0xf: // EFx — ProTracker invert loop ("funk repeat")
        if (this.song.mode === 'pt') ch.funkSpeed = y;
        break;

      default:
        break;
    }
  }

  private sampleOffset(ch: Channel, param: number): void {
    if (param !== 0) ch.memSampleOffset = param;
    if (!ch.sample) return;
    const offset = ch.memSampleOffset * 256;
    if (offset >= ch.sample.length) {
      // ProTracker runs past the end and wraps to the start; FT2 silences it.
      if (this.song.mode === 'pt') ch.position = 0;
      else ch.stop();
      return;
    }
    ch.position = offset;
  }

  // =======================================================================
  // Effects — ticks 1..speed-1
  // =======================================================================

  private effectTick(ch: Channel, effect: number, param: number): void {
    const x = param >> 4;
    const y = param & 0x0f;

    switch (effect) {
      case 0x00: // Arpeggio
        if (ch.memArpeggio !== 0) this.arpeggio(ch);
        break;

      case 0x01:
        ch.period = this.clampPeriod(ch.period - this.portaScale() * ch.memPortaUp);
        break;

      case 0x02:
        ch.period = this.clampPeriod(ch.period + this.portaScale() * ch.memPortaDown);
        break;

      case 0x03:
        this.tonePortamento(ch);
        break;

      case 0x04:
        this.vibrato(ch);
        break;

      case 0x05:
        this.tonePortamento(ch);
        this.volumeSlide(ch, ch.memVolumeSlide);
        break;

      case 0x06:
        this.vibrato(ch);
        this.volumeSlide(ch, ch.memVolumeSlide);
        break;

      case 0x07:
        this.tremolo(ch);
        break;

      case 0x0a:
        this.volumeSlide(ch, ch.memVolumeSlide);
        break;

      case 0x0e:
        this.extendedTick(ch, x, y);
        break;

      case 0x11: // Hxy — global volume slide
        if (this.song.mode === 'ft2') {
          const up = ch.memGlobalVolumeSlide >> 4;
          const down = ch.memGlobalVolumeSlide & 0x0f;
          if (up > 0) this.globalVolume = Math.min(64, this.globalVolume + up);
          else if (down > 0) this.globalVolume = Math.max(0, this.globalVolume - down);
        }
        break;

      case 0x14: // Kxx — key off on tick xx
        if (this.song.mode === 'ft2' && this.tick === param) this.noteOff(ch);
        break;

      case 0x19: // Pxy — panning slide
        if (this.song.mode === 'ft2') {
          const right = ch.memPanningSlide >> 4;
          const left = ch.memPanningSlide & 0x0f;
          if (right > 0) ch.panning = Math.min(255, ch.panning + right);
          else if (left > 0) ch.panning = Math.max(0, ch.panning - left);
        }
        break;

      case 0x1b: // Rxy — multi retrigger
        if (this.song.mode === 'ft2') this.multiRetrig(ch);
        break;

      case 0x1d: // Txy — tremor
        if (this.song.mode === 'ft2') this.tremor(ch);
        break;

      default:
        break;
    }
  }

  private extendedTick(ch: Channel, x: number, y: number): void {
    switch (x) {
      case 0x9: // E9x — retrigger every y ticks
        if (y > 0 && this.tick % y === 0) {
          ch.position = 0;
          ch.direction = 1;
          ch.playing = ch.sample !== null;
          if (this.song.mode === 'ft2') this.resetEnvelopes(ch);
        }
        break;

      case 0xc: // ECx — note cut
        if (this.tick === y) ch.volume = 0;
        break;

      default:
        break;
    }
  }

  // ---- Individual effect implementations ---------------------------------

  private arpeggio(ch: Channel): void {
    const param = ch.memArpeggio;
    const step = this.tick % 3;
    const semis = step === 0 ? 0 : step === 1 ? param >> 4 : param & 0x0f;
    ch.outPeriod =
      semis === 0 ? ch.period : this.noteToPeriod(ch.note + ch.relativeNote + semis, ch.finetune);
    ch.pitchModulated = true;
  }

  private tonePortamento(ch: Channel): void {
    if (ch.portaTarget === 0) return;
    const step = this.portaScale() * ch.memTonePorta;
    if (ch.period < ch.portaTarget) {
      ch.period = Math.min(ch.portaTarget, ch.period + step);
    } else if (ch.period > ch.portaTarget) {
      ch.period = Math.max(ch.portaTarget, ch.period - step);
    }
    ch.period = this.clampPeriod(ch.period);
    if (ch.glissando) {
      ch.outPeriod = this.snapToSemitone(ch, ch.period);
      ch.pitchModulated = true;
    }
  }

  /**
   * ProTracker and FastTracker II share the waveform table and the position
   * stepping, but scale the result differently: >>7 against MOD's small periods,
   * >>5 against XM's four-times-larger ones. That one shift is what makes
   * vibrato depth sound the same in both.
   */
  private vibrato(ch: Channel): void {
    const depth = ch.memVibratoDepth;
    const speed = ch.memVibratoSpeed;
    const idx = (ch.vibratoPos >> 2) & 0x1f;
    let value: number;

    switch (ch.vibratoWaveform) {
      case 1: // ramp
        value = (idx << 3) & 0xff;
        break;
      case 2: // square
      case 3: // random — FT2 approximates it with a square
        value = 255;
        break;
      default: // sine
        value = PT_SINE_TABLE[idx];
        break;
    }

    const shift = this.song.mode === 'pt' ? 7 : 5;
    const delta = (value * depth) >> shift;
    ch.outPeriod = ch.vibratoPos & 0x80 ? ch.period - delta : ch.period + delta;
    ch.pitchModulated = true;
    ch.vibratoPos = (ch.vibratoPos + speed * 4) & 0xff;
  }

  private tremolo(ch: Channel): void {
    const depth = ch.memTremoloDepth;
    const speed = ch.memTremoloSpeed;
    const idx = (ch.tremoloPos >> 2) & 0x1f;
    let value: number;

    switch (ch.tremoloWaveform) {
      case 1:
        value = (idx << 3) & 0xff;
        break;
      case 2:
      case 3:
        value = 255;
        break;
      default:
        value = PT_SINE_TABLE[idx];
        break;
    }

    const delta = (value * depth) >> 6;
    ch.tremoloVolume = ch.tremoloPos & 0x80 ? -delta : delta;
    ch.tremoloPos = (ch.tremoloPos + speed * 4) & 0xff;
  }

  private volumeSlide(ch: Channel, param: number): void {
    const up = param >> 4;
    const down = param & 0x0f;
    // With both nibbles set, up wins — true of both replayers.
    if (up > 0) ch.volume = Math.min(64, ch.volume + up);
    else if (down > 0) ch.volume = Math.max(0, ch.volume - down);
  }

  private multiRetrig(ch: Channel): void {
    const interval = ch.memRetrig & 0x0f;
    const volumeCase = ch.memRetrig >> 4;
    if (interval === 0 || this.tick % interval !== 0) return;

    ch.position = 0;
    ch.direction = 1;
    ch.playing = ch.sample !== null;
    if (volumeCase > 0) {
      ch.volume = Math.max(0, Math.min(64, Math.floor(RETRIG_VOLUME[volumeCase](ch.volume))));
    }
    this.resetEnvelopes(ch);
  }

  /**
   * Tremor gates the channel on for x+1 ticks and off for y+1. The channel
   * starts muted with a zero counter so the first tick flips it on, which is how
   * the phase lines up with FT2.
   */
  private tremor(ch: Channel): void {
    const onTicks = (ch.memTremor >> 4) + 1;
    const offTicks = (ch.memTremor & 0x0f) + 1;
    if (ch.tremorCount <= 0) {
      ch.tremorOn = !ch.tremorOn;
      ch.tremorCount = ch.tremorOn ? onTicks : offTicks;
    }
    ch.tremorCount--;
    ch.tremorMute = !ch.tremorOn;
  }

  /**
   * ProTracker's EFx bit-inverts bytes of the looped sample as it plays, which
   * is why it is remembered as a glitch effect rather than a musical one. It
   * really does rewrite sample data, so loaders hand the player its own copy.
   */
  private funkRepeat(ch: Channel): void {
    if (this.song.mode !== 'pt' || ch.funkSpeed === 0 || !ch.sample) return;
    ch.funkPos += FUNK_TABLE[ch.funkSpeed & 0x0f];
    if (ch.funkPos < 128) return;
    ch.funkPos = 0;
    const s = ch.sample;
    if (s.loopType === LOOP_NONE || s.loopLength <= 0) return;
    ch.funkOffset = (ch.funkOffset + 1) % s.loopLength;
    const idx = s.loopStart + ch.funkOffset;
    if (idx < s.data.length) s.data[idx] = -s.data[idx] - 1 / 128;
  }

  // ---- Volume column (XM) -------------------------------------------------

  private volumeColumnTick0(ch: Channel, v: number): void {
    if (v >= 0x10 && v <= 0x50) {
      ch.volume = v - 0x10;
    } else if (v >= 0x80 && v <= 0x8f) {
      ch.volume = Math.max(0, ch.volume - (v & 0x0f));
    } else if (v >= 0x90 && v <= 0x9f) {
      ch.volume = Math.min(64, ch.volume + (v & 0x0f));
    } else if (v >= 0xa0 && v <= 0xaf) {
      ch.memVibratoSpeed = v & 0x0f;
    } else if (v >= 0xc0 && v <= 0xcf) {
      ch.panning = (v & 0x0f) * 17;
    } else if (v >= 0xf0) {
      const p = (v & 0x0f) * 16;
      if (p !== 0) ch.memTonePorta = p;
    }
  }

  private volumeColumnTick(ch: Channel, v: number): void {
    if (v >= 0x60 && v <= 0x6f) {
      ch.volume = Math.max(0, ch.volume - (v & 0x0f));
    } else if (v >= 0x70 && v <= 0x7f) {
      ch.volume = Math.min(64, ch.volume + (v & 0x0f));
    } else if (v >= 0xb0 && v <= 0xbf) {
      const depth = v & 0x0f;
      if (depth !== 0) ch.memVibratoDepth = depth;
      this.vibrato(ch);
    } else if (v >= 0xd0 && v <= 0xdf) {
      ch.panning = Math.max(0, ch.panning - (v & 0x0f));
    } else if (v >= 0xe0 && v <= 0xef) {
      ch.panning = Math.min(255, ch.panning + (v & 0x0f));
    } else if (v >= 0xf0) {
      this.tonePortamento(ch);
    }
  }

  // =======================================================================
  // Per-tick channel update: envelopes, fadeout, final gains
  // =======================================================================

  private updateChannels(): void {
    for (const ch of this.renderChannels) this.updateChannel(ch);
  }

  /**
   * Advance only the preview voices. The transport can be stopped while the user
   * is playing the keyboard, and those notes still need their envelopes and
   * fadeouts ticked.
   */
  tickPreview(): void {
    for (const ch of this.previewChannels) this.updateChannel(ch);
  }

  private updateChannel(ch: Channel): void {
    {
      // Only a pitch-modulating effect this tick makes outPeriod meaningful;
      // otherwise the base period is what sounds.
      let period = ch.pitchModulated ? ch.outPeriod : ch.period;

      if (this.song.mode === 'ft2') {
        this.updateEnvelopes(ch);
        period += this.autoVibratoDelta(ch);
      }

      ch.outPeriod = period;
      ch.frequency = this.periodToFrequency(period);

      let vol = ch.volume + ch.tremoloVolume;
      if (vol < 0) vol = 0;
      if (vol > 64) vol = 64;

      let gain = vol / 64;
      if (this.song.mode === 'ft2') {
        gain *= ch.volumeEnvelopeValue / 64;
        gain *= ch.fadeoutVolume / 65536;
      }
      if (ch.tremorMute) gain = 0;
      gain *= this.globalVolume / 64;

      ch.outVolume = ch.playing ? gain : 0;

      // The panning envelope narrows towards centre the further out the base
      // panning already sits — FT2's formula.
      let pan = ch.panning;
      if (this.song.mode === 'ft2' && ch.instrument?.panningEnvelope.enabled) {
        pan += ((ch.panningEnvelopeValue - 32) * (128 - Math.abs(pan - 128))) / 32;
      }
      ch.outPanning = Math.max(0, Math.min(255, pan)) / 255;
    }
  }

  private updateEnvelopes(ch: Channel): void {
    const inst = ch.instrument;
    if (!inst) {
      ch.volumeEnvelopeValue = 64;
      ch.panningEnvelopeValue = 32;
      return;
    }

    ch.volumeEnvelopeValue = this.stepEnvelope(inst.volumeEnvelope, ch, 'volumeEnvelopeTick', 64);
    ch.panningEnvelopeValue = this.stepEnvelope(inst.panningEnvelope, ch, 'panningEnvelopeTick', 32);

    if (ch.keyOff) {
      ch.fadeoutVolume -= inst.volumeFadeout;
      if (ch.fadeoutVolume <= 0) {
        ch.fadeoutVolume = 0;
        ch.playing = false;
      }
    }
  }

  private stepEnvelope(
    env: Envelope,
    ch: Channel,
    tickKey: 'volumeEnvelopeTick' | 'panningEnvelopeTick',
    fallback: number,
  ): number {
    if (!env.enabled || env.points.length === 0) return fallback;

    const pos = ch[tickKey];
    const value = envelopeValueAt(env, pos);
    const pts = env.points;

    const sustainX = pts[Math.min(env.sustainPoint, pts.length - 1)]?.x ?? 0;
    if (env.sustainEnabled && !ch.keyOff && pos >= sustainX) {
      ch[tickKey] = sustainX;
      return value;
    }

    const loopEndX = pts[Math.min(env.loopEnd, pts.length - 1)]?.x ?? 0;
    const loopStartX = pts[Math.min(env.loopStart, pts.length - 1)]?.x ?? 0;
    if (env.loopEnabled && pos >= loopEndX) {
      ch[tickKey] = loopStartX;
      return value;
    }

    if (pos < pts[pts.length - 1].x) ch[tickKey] = pos + 1;
    return value;
  }

  private autoVibratoDelta(ch: Channel): number {
    const inst = ch.instrument;
    if (!inst || inst.vibratoDepth === 0) return 0;

    // The sweep ramps depth in from zero over `vibratoSweep` ticks.
    let depth = inst.vibratoDepth;
    if (inst.vibratoSweep > 0 && ch.autoVibratoSweep < inst.vibratoSweep) {
      depth = (depth * ch.autoVibratoSweep) / inst.vibratoSweep;
      ch.autoVibratoSweep++;
    }

    ch.autoVibratoPos = (ch.autoVibratoPos + inst.vibratoRate) & 0xff;
    const idx = (ch.autoVibratoPos >> 2) & 0x1f;
    let value: number;
    switch (inst.vibratoType) {
      case 1:
        value = 255;
        break;
      case 2:
        value = (idx << 3) & 0xff;
        break;
      case 3:
        value = 255 - ((idx << 3) & 0xff);
        break;
      default:
        value = PT_SINE_TABLE[idx];
        break;
    }
    const delta = (value * depth) >> 7;
    return ch.autoVibratoPos & 0x80 ? -delta : delta;
  }
}

/** Linear interpolation of an envelope at a tick position. */
export function envelopeValueAt(env: Envelope, x: number): number {
  const pts = env.points;
  if (pts.length === 0) return 0;
  if (x <= pts[0].x) return pts[0].y;
  const last = pts[pts.length - 1];
  if (x >= last.x) return last.y;
  for (let i = 0; i < pts.length - 1; i++) {
    const a = pts[i];
    const b = pts[i + 1];
    if (x >= a.x && x <= b.x) {
      const span = b.x - a.x;
      if (span <= 0) return b.y;
      return a.y + ((b.y - a.y) * (x - a.x)) / span;
    }
  }
  return last.y;
}
